// src/services/studyCloseService.js
//
// When an admin closes a study, the patients enrolled in it are offered back to
// Lung Center Referrals. This is the I/O half; the decision is planStudyClose in
// enrollmentRules.js, which is Firebase-free and tested.
//
// Scope and safety:
//   - Active site only: consents via firestorePaths, plus the legacy Sheet only
//     when this site uses it.
//   - Nothing moves until the admin confirms in StudyClosePatientsModal.
//   - Each Firestore patient is re-read right before writing and re-planned from
//     that fresh copy, so anyone who changed status in the meantime is skipped.
//     Sheet rows get the same guard server-side (expectStatus: "Enrolled").
//   - Only `enrolledStudies`, `status` and lastEdited* are written. The closed
//     study's entry stays on the record with closedAt, so "was enrolled in X"
//     is never lost.
//   - Every patient touched gets an audit event (fail-soft).

import { collection, doc, getDoc, getDocs, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { getConsentsPath } from "./firestorePaths";
import { activeSiteUsesLegacySheet } from "./activeTenant";
import { fetchSheetPatients, updateSheetEnrollment } from "./sheetPatientsService";
import { fetchStudies } from "./studyService";
import { logPatientAuditEvent, getMrnLast4 } from "./patientAuditService";
import {
  planStudyClose,
  studyDisplayName,
  REFERRALS_STATUS,
  ENROLLED_STATUS,
  RETURN_REASON_TEXT,
} from "./enrollmentRules";

const actor = () => ({
  actorEmail: auth.currentUser?.email || "unknown",
  actorUid: auth.currentUser?.uid || null,
});

/**
 * Load everyone the close affects and what happens to each.
 * @returns {Promise<{plans: Array, allStudies: Array, sheetError: boolean}>}
 *   Each plan also carries `patient.source` ("firestore" | "sheet") and
 *   `patient.name`. `sheetError` means the Sheet couldn't be read, so Sheet
 *   patients may be missing from the list.
 */
export async function loadStudyClosePlans(study) {
  const at = new Date().toISOString();
  const by = auth.currentUser?.email || "unknown";

  const [allStudies, consentsSnap] = await Promise.all([
    fetchStudies(),
    getDocs(collection(db, getConsentsPath())),
  ]);

  const patients = consentsSnap.docs.map((d) => {
    const p = d.data();
    return {
      id: d.id,
      source: "firestore",
      name: p.fullName || p.name || "",
      mrn: p.mrn || "",
      status: p.status || "",
      enrolledStudies: p.enrolledStudies,
    };
  });

  let sheetError = false;
  if (activeSiteUsesLegacySheet()) {
    try {
      const rows = await fetchSheetPatients();
      rows.forEach((r) =>
        patients.push({
          id: r.token,
          source: "sheet",
          name: r.fullName || r.name || r.initials || "",
          mrn: r.mrn || "",
          status: r.status || "",
          enrolledStudies: r.enrolledStudies,
        })
      );
    } catch (err) {
      console.error("Study close: couldn't read the patient sheet", err);
      sheetError = true;
    }
  }

  return { plans: planStudyClose(patients, study, allStudies, { at, by }), allStudies, sheetError };
}

const auditPlan = async (plan, study, recordPath) => {
  const p = plan.patient;
  const base = {
    patientId: p.id,
    patientName: p.name,
    mrnLast4: getMrnLast4(p.mrn),
    source: p.source,
    ...actor(),
  };
  const studyName = studyDisplayName(study);
  const metadata = { page: "StudyClosePatientsModal", recordPath, studyId: study.id, studyName };
  if (plan.move) {
    await logPatientAuditEvent({
      ...base,
      title: "Returned to Lung Center Referrals",
      action: "RETURNED_TO_REFERRALS",
      changes: [{ field: "status", from: ENROLLED_STATUS, to: REFERRALS_STATUS }],
      metadata: {
        ...metadata,
        reason: plan.reason,
        reasonText: RETURN_REASON_TEXT[plan.reason] || null,
        trigger: "study_closed",
      },
    });
  } else {
    await logPatientAuditEvent({
      ...base,
      title: "Enrolled Study Closed",
      action: "ENROLLED_STUDY_CLOSED",
      changes: [{ field: "enrolledStudies", from: `${studyName} (open)`, to: `${studyName} (closed)` }],
      metadata: {
        ...metadata,
        staysEnrolledIn: plan.keeping.map((e) => e.studyName || e.studyId),
        ...(plan.unsure ? { waitingOnVerification: true } : {}),
      },
    });
  }
};

/**
 * Apply confirmed plans. Returns a per-patient result so the pop-up can say
 * exactly what happened; one failure never stops the rest.
 * @returns {Promise<Array<{id, name, ok, moved, skipped?, error?}>>}
 */
export async function applyStudyClosePlans(plans, study, allStudies) {
  const at = new Date().toISOString();
  const by = auth.currentUser?.email || "unknown";
  const results = [];

  // Firestore patients: re-read, re-plan from the fresh copy, then write.
  for (const plan of plans.filter((x) => x.patient.source === "firestore")) {
    const p = plan.patient;
    try {
      const ref = doc(db, getConsentsPath(), p.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        results.push({ id: p.id, name: p.name, ok: false, error: "Record not found." });
        continue;
      }
      const fresh = snap.data();
      const [freshPlan] = planStudyClose(
        [{ ...p, status: fresh.status || "", enrolledStudies: fresh.enrolledStudies }],
        study,
        allStudies,
        { at, by }
      );
      if (!freshPlan) {
        results.push({ id: p.id, name: p.name, ok: true, moved: false, skipped: "Changed since the list loaded; left as is." });
        continue;
      }
      await updateDoc(ref, {
        enrolledStudies: freshPlan.nextEntries,
        ...(freshPlan.move ? { status: REFERRALS_STATUS } : {}),
        lastEditedBy: by,
        lastEditedAt: serverTimestamp(),
      });
      await auditPlan(freshPlan, study, `consents/${p.id}`);
      results.push({ id: p.id, name: p.name, ok: true, moved: freshPlan.move });
    } catch (err) {
      console.error("Study close: failed to update", p.id, err);
      results.push({ id: p.id, name: p.name, ok: false, error: "Couldn't save." });
    }
  }

  // Sheet patients: one narrow call; the server merges and guards status.
  const sheetPlans = plans.filter((x) => x.patient.source === "sheet");
  if (sheetPlans.length) {
    try {
      const replies = await updateSheetEnrollment(
        sheetPlans.map((plan) => ({
          token: plan.patient.id,
          enrolledStudies: plan.nextEntries,
          ...(plan.move ? { status: REFERRALS_STATUS, expectStatus: ENROLLED_STATUS } : {}),
        }))
      );
      const byToken = new Map(replies.map((r) => [r.token, r]));
      for (const plan of sheetPlans) {
        const p = plan.patient;
        const r = byToken.get(p.id);
        if (!r || !r.ok) {
          results.push({ id: p.id, name: p.name, ok: false, error: (r && r.error) || "No reply for this row." });
          continue;
        }
        if (r.statusSkipped) {
          // Enrollment was recorded; status had changed since, so it was left alone.
          await auditPlan({ ...plan, move: false }, study, `sheet/${p.id}`);
          results.push({ id: p.id, name: p.name, ok: true, moved: false, skipped: "Status changed since the list loaded; left as is." });
          continue;
        }
        await auditPlan(plan, study, `sheet/${p.id}`);
        results.push({ id: p.id, name: p.name, ok: true, moved: plan.move });
      }
    } catch (err) {
      console.error("Study close: sheet update failed", err);
      sheetPlans.forEach((plan) =>
        results.push({ id: plan.patient.id, name: plan.patient.name, ok: false, error: "The patient sheet is unavailable." })
      );
    }
  }

  return results;
}
